import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../styles/detail.css';

const API_URL = '';

// 동영상/GIF URL → 상대경로 그대로 사용 (dev: proxy, prod: nginx → 백엔드)
const resolveMediaUrl = (url: string) => url || '';

interface ProductData {
  id: number;
  name: string;
  images: string[];
  price: number;
  originalPrice: number;
  discount: number;
  rating: number | null;
  reviews: number | null;
  sold: string | null;
  category: string;
  description: string;
  descriptionImages: string[];
  options: { name: string; values: { label: string; image: string; available: boolean; price?: number; stock?: number }[] }[];
  shipping: string;
  shippingFee?: number;
  freeShippingQty?: number;
  affiliateLink?: string;
  videoUrl?: string;
  specTable?: { key: string; value: string }[];
  brandTopImage?: string;
  brandBottomImage?: string;
}

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<ProductData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(-1);
  const [quantity, setQuantity] = useState(1);
  const [cartMsg, setCartMsg] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<{ [key: string]: number }>({});
  const [optionImage, setOptionImage] = useState<string | null>(null);
  const [descExpanded, setDescExpanded] = useState(false);
  const [brandImages, setBrandImages] = useState<{ top: string; bottom: string }>({ top: '', bottom: '' });
  // 리뷰 & Q&A
  const [reviews, setReviews] = useState<{ nickname: string; rating: number; text: string; date: string }[]>([]);
  const [reviewText, setReviewText] = useState('');
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewNick, setReviewNick] = useState('');
  const [qnas, setQnas] = useState<{ nickname: string; question: string; answer: string; date: string }[]>([]);
  const [qnaText, setQnaText] = useState('');
  const [qnaNick, setQnaNick] = useState('');
  const { addToCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    const abortCtrl = new AbortController();
    const signal = abortCtrl.signal;
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const [res, brandRes] = await Promise.all([
          fetch(`${API_URL}/api/storefront/detail/${id}`, { signal }),
          fetch(`${API_URL}/api/brand-images/status`, { signal })
        ]);
        if (signal.aborted) return;
        const data = await res.json();
        const brand = await brandRes.json();
        setProduct(data);
        setSelectedImage(data.videoUrl ? -1 : 0);
        setBrandImages({
          top: data.brandTopImage || brand.brand_top_url || '',
          bottom: data.brandBottomImage || brand.brand_bottom_url || ''
        });
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        console.error('Failed to fetch product:', err);
      }
      if (!signal.aborted) setLoading(false);
    };
    fetchProduct();
    window.scrollTo(0, 0);
    setDescExpanded(false);
    // 리뷰 & Q&A 로드 (AbortController 적용)
    fetch(`${API_URL}/api/storefront/reviews/${id}`, { signal }).then(r => r.json()).then(d => { if (!signal.aborted) setReviews(d || []); }).catch(() => {});
    fetch(`${API_URL}/api/storefront/qna/${id}`, { signal }).then(r => r.json()).then(d => { if (!signal.aborted) setQnas(d || []); }).catch(() => {});
    return () => abortCtrl.abort();
  }, [id]);

  // SEO: 페이지별 메타태그 동적 설정 (Hooks는 early return 앞에 선언)
  useEffect(() => {
    if (!product) return;
    document.title = `${product.name} - 월드쉽`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', `${product.name} - ${product.price?.toLocaleString()}원 | 월드쉽 해외직구`);
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', product.name);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', `${product.price?.toLocaleString()}원 - 월드쉽 해외직구 최저가`);
    if (product.images && product.images[0]) {
      const ogImg = document.querySelector('meta[property="og:image"]');
      if (ogImg) ogImg.setAttribute('content', product.images[0]);
    }
    return () => { document.title = '월드쉽 (WORLDSHIP) - 해외직구 최저가 쇼핑몰'; };
  }, [product, id]);

  const formatPrice = (price: number) => price.toLocaleString() + '\uC6D0';

  // 선택된 옵션의 가격 계산
  const getCurrentPrice = () => {
    if (!product) return 0;
    for (const key of Object.keys(selectedOptions)) {
      const idx = selectedOptions[key];
      const opt = product.options?.find(o => o.name === key);
      const p = opt?.values?.[idx]?.price;
      if (p && p > 0) return p;
    }
    return product.price;
  };

  if (loading) {
    return (
      <div className="App">
        <Header />
        <div style={{ textAlign: 'center', padding: 100, color: '#999' }}>Loading...</div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="App">
        <Header />
        <div style={{ textAlign: 'center', padding: 100 }}>Product not found</div>
        <Footer />
      </div>
    );
  }

  // Schema.org Product 구조화 데이터 (구글 쇼핑 노출)
  const schemaProduct = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "image": product.images.length > 0 ? product.images[0] : undefined,
    "description": product.description?.substring(0, 200) || product.name,
    "brand": { "@type": "Brand", "name": "월드쉽 (WORLDSHIP)" },
    "offers": {
      "@type": "Offer",
      "url": `https://worldship.kr/shop/product/${id}`,
      "priceCurrency": "KRW",
      "price": product.price,
      "availability": "https://schema.org/InStock",
      "seller": { "@type": "Organization", "name": "월드쉽 (WORLDSHIP)" }
    }
  };

  return (
    <div className="App">
      <Header />
      {/* SEO: Schema.org Product JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaProduct) }} />

      <div className="detail-page">
        <div className="detail-breadcrumb">
          <Link to="/">홈</Link> &gt; <span>{product.category || '상품상세'}</span>
        </div>

        <div className="detail-content">
          {/* Left: Images */}
          <div className="detail-images">
            <div className="detail-main-image">
              {selectedImage === -2 && optionImage ? (
                <img src={optionImage} alt="option" />
              ) : selectedImage === -1 && product.videoUrl ? (
                product.videoUrl.includes('.mp4') || product.videoUrl.includes('video') ? (
                  <video
                    src={resolveMediaUrl(product.videoUrl)}
                    poster={product.images[0] || ''}
                    autoPlay loop muted playsInline
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    onError={() => setSelectedImage(0)}
                  />
                ) : (
                  <img src={product.videoUrl} alt={product.name}
                    onError={() => setSelectedImage(0)} />
                )
              ) : (
                <img
                  src={product.images[Math.max(0, selectedImage)] || 'https://via.placeholder.com/500x500?text=No+Image'}
                  alt={product.name}
                />
              )}
            </div>
            <div className="detail-thumbnails">
              {product.videoUrl && (
                <div
                  className={`detail-thumb-video ${selectedImage === -1 ? 'active' : ''}`}
                  onClick={() => setSelectedImage(-1)}
                >
                  {product.videoUrl.includes('.gif') ? (
                    <img src={product.videoUrl} alt="GIF" />
                  ) : (
                    <>
                      <img src={product.images[0]} alt="video" />
                      <div className="thumb-play-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff"><path d="M8 5v14l11-7z"/></svg>
                      </div>
                    </>
                  )}
                </div>
              )}
              {product.images.map((img, i) => (
                <img
                  key={i}
                  src={img}
                  alt={`thumb-${i}`}
                  className={selectedImage === i ? 'active' : ''}
                  onClick={() => setSelectedImage(i)}
                  loading="lazy"
                />
              ))}
            </div>
          </div>

          {/* Right: Info */}
          <div className="detail-info">
            <h1 className="detail-title">{product.name}</h1>

            <div className="detail-rating">
              {!!product.rating && product.rating > 0 && (
                <>
                  <span className="stars">
                    {'\u2605'.repeat(Math.floor(product.rating))}
                  </span>
                  <span className="rating-num">{product.rating}</span>
                </>
              )}
              {product.sold && <span className="sold-count">{product.sold} sold</span>}
            </div>

            <div className="detail-price-box">
              <span className="detail-current-price">{formatPrice(getCurrentPrice())}</span>
              {product.originalPrice > getCurrentPrice() && (
                <>
                  <span className="detail-original-price">{formatPrice(product.originalPrice)}</span>
                  <span className="detail-discount">-{Math.round((1 - getCurrentPrice() / product.originalPrice) * 100)}%</span>
                </>
              )}
            </div>

            {/* Options */}
            {product.options.map((opt, oi) => (
              <div className="detail-section" key={oi}>
                <div className="detail-label">
                  {opt.name}
                  {selectedOptions[opt.name] !== undefined && (
                    <span className="detail-label-selected">: {opt.values[selectedOptions[opt.name]]?.label}</span>
                  )}
                </div>
                <div className="detail-options">
                  {opt.values.map((val, vi) => (
                    <button
                      key={vi}
                      className={`option-btn ${selectedOptions[opt.name] === vi ? 'selected' : ''} ${!val.available ? 'disabled' : ''}`}
                      title={val.label}
                      onClick={() => { setSelectedOptions(prev => ({ ...prev, [opt.name]: vi })); if (val.image) { setOptionImage(val.image); setSelectedImage(-2); } }}
                    >
                      {val.image ? (
                        <img src={val.image} alt={val.label} style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4 }} />
                      ) : null}
                      <span className="option-label">{val.label}</span>
                      {val.price && val.price > 0 && (
                        <span className="option-price">{formatPrice(val.price)}</span>
                      )}
                    </button>
                  ))}
                </div>
                {selectedOptions[opt.name] !== undefined && (() => {
                  const sel = opt.values[selectedOptions[opt.name]];
                  if (sel?.stock !== undefined && sel.stock > 0 && sel.stock <= 10) {
                    return <div className="detail-stock-warn">재고 {sel.stock}개 남음</div>;
                  }
                  if (sel?.stock === 0) {
                    return <div className="detail-stock-warn out">품절</div>;
                  }
                  return null;
                })()}
              </div>
            ))}

            <div className="detail-section">
              <div className="detail-label">수량</div>
              <div className="detail-quantity">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))}>-</button>
                <span>{quantity}</span>
                <button onClick={() => setQuantity(quantity + 1)}>+</button>
              </div>
            </div>

            <div className="detail-section">
              <div className="detail-label">배송</div>
              <div className="detail-shipping">
                {product.shippingFee && product.shippingFee > 0
                  ? <>
                      <span style={{ color: '#e62e04', fontWeight: 700 }}>배송비 {product.shippingFee.toLocaleString()}원</span>
                      <span style={{ color: '#888' }}> (7~15일)</span>
                      {product.shippingFee === 1500 && (
                        <span style={{ color: '#00b612', fontWeight: 600, marginLeft: 8, fontSize: 13 }}>
                          2개 이상 무료배송 <span style={{ color: '#aaa', fontWeight: 400 }}>(일부 상품 제외)</span>
                        </span>
                      )}
                    </>
                  : <span style={{ color: '#00b612', fontWeight: 600 }}>무료배송 (7~15일)</span>
                }
                {/* 해외 구매대행 안내(통관 · 관세 · 반품) - 서버가 내려 주는 안내 글에서 배송비 부분을 뺀 나머지 */}
                {product.shipping && product.shipping.includes(' · ') && (
                  <div style={{ color: '#888', fontSize: 12, lineHeight: 1.6, marginTop: 6 }}>
                    {product.shipping.split(' · ').slice(1).join(' · ')}
                  </div>
                )}
              </div>
            </div>

            {cartMsg && <div className={`detail-cart-msg ${cartMsg.includes('추가') ? 'success' : 'error'}`}>{cartMsg}</div>}
            <div className="detail-actions">
              <button className="btn-buy" onClick={() => {
                const optNames = Object.keys(selectedOptions).map(k => {
                  const opt = product.options?.find(o => o.name === k);
                  return opt?.values?.[selectedOptions[k]]?.label;
                }).filter(Boolean).join(' / ');
                navigate('/order', {
                  state: {
                    items: [{
                      product_id: product.id,
                      product_name: product.name,
                      main_image: product.images?.[0] || '',
                      price: getCurrentPrice(),
                      quantity,
                      option_name: optNames || undefined
                    }],
                    shippingFee: product.shippingFee === 1500
                      ? (quantity >= 2 ? 0 : 1500)
                      : (product.shippingFee || 0) * quantity
                  }
                });
              }}>구매하기</button>
              <button className="btn-cart" onClick={async () => {
                const optNames = Object.keys(selectedOptions).map(k => {
                  const opt = product.options?.find(o => o.name === k);
                  return opt?.values?.[selectedOptions[k]]?.label;
                }).filter(Boolean).join(' / ');
                const result = await addToCart(product.id, getCurrentPrice(), optNames || undefined, quantity);
                setCartMsg(result.success ? '장바구니에 추가되었습니다!' : (result.error || '추가 실패'));
                setTimeout(() => setCartMsg(''), 2000);
              }}>장바구니</button>
            </div>
          </div>
        </div>

        {/* Bottom: Description */}
        <div className="detail-bottom">
          <div className="detail-tabs">
            <span className="tab active">상품상세</span>
            <span className="tab">상품평</span>
          </div>

          <div className="detail-description" style={{
            maxHeight: descExpanded ? 'none' : 800,
            overflow: descExpanded ? 'visible' : 'hidden',
            position: 'relative',
            transition: 'max-height 0.3s ease',
          }}>
            {/* 브랜드 상단 이미지 */}
            {brandImages.top && (
              <img src={brandImages.top} alt="brand-top" className="desc-image" style={{ marginBottom: 24 }} loading="lazy" />
            )}

            {/* 상품 상세 스펙 테이블 */}
            {product.specTable && product.specTable.length > 0 && (
              <div style={{ marginBottom: 24, maxWidth: 800, margin: '0 auto 24px auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead>
                    <tr>
                      <td colSpan={2} style={{ background: '#f47920', color: '#fff', textAlign: 'center', padding: '10px', fontSize: 16, fontWeight: 700 }}>
                        상품 상세 스펙
                      </td>
                    </tr>
                  </thead>
                  <tbody>
                    {product.specTable.map((row, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? '#f9f9f9' : '#fff' }}>
                        <td style={{ padding: '8px 12px', border: '1px solid #ddd', fontWeight: 700, width: '35%', color: '#333' }}>{row.key}</td>
                        <td style={{ padding: '8px 12px', border: '1px solid #ddd', color: '#555' }}>{row.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* 스펙 테이블 없으면 HTML 설명 표시 */}
            {(!product.specTable || product.specTable.length === 0) && product.description && (
              <div dangerouslySetInnerHTML={{ __html: product.description }} />
            )}

            {/* 동영상/GIF - 상세 이미지 위에 표시 */}
            {product.videoUrl && (
              <div className="desc-video">
                {product.videoUrl.includes('.mp4') || product.videoUrl.includes('video') ? (
                  <video
                    src={resolveMediaUrl(product.videoUrl)}
                    poster={product.images[0] || ''}
                    autoPlay loop muted playsInline controls
                    style={{ width: '100%', maxWidth: 800, display: 'block', margin: '20px auto', borderRadius: 8 }}
                  />
                ) : (
                  <img
                    src={product.videoUrl}
                    alt="product-gif"
                    style={{ width: '100%', maxWidth: 800, display: 'block', margin: '20px auto', borderRadius: 8 }}
                  />
                )}
              </div>
            )}

            {/* Description Images from Ali */}
            {product.descriptionImages && product.descriptionImages.length > 0 && (
              <div className="desc-images">
                {product.descriptionImages.map((img, i) => (
                  <img key={i} src={img} alt={`desc-${i}`} className="desc-image" loading="lazy" />
                ))}
              </div>
            )}

            {/* 브랜드 하단 이미지 */}
            {brandImages.bottom && (
              <img src={brandImages.bottom} alt="brand-bottom" className="desc-image" style={{ marginTop: 24 }} loading="lazy" />
            )}

            {/* 접힌 상태 그라데이션 오버레이 */}
            {!descExpanded && (
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0, height: 120,
                background: 'linear-gradient(transparent, white)',
                pointerEvents: 'none',
              }} />
            )}
          </div>

          {/* 상세정보 펼쳐보기 / 접기 버튼 */}
          <div
            onClick={() => setDescExpanded(!descExpanded)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '18px 0', cursor: 'pointer', border: '2px solid #e53e3e', borderRadius: 8,
              maxWidth: 900, margin: '0 auto', fontSize: 20, fontWeight: 700, color: '#e53e3e',
              background: '#fff5f5', transition: 'background 0.2s',
            }}
          >
            {descExpanded ? '상세정보 접기' : '상세정보 펼쳐보기'}
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#e53e3e"
              style={{ transform: descExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }}>
              <path d="M7 10l5 5 5-5z"/>
            </svg>
          </div>
        </div>

        {/* 상품 리뷰 */}
        <div style={{ maxWidth: 900, margin: '40px auto 0', padding: '0 20px' }}>
          <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>상품리뷰</h3>
          <p style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>
            상품을 구매하신 분들이 작성하신 리뷰입니다. 리뷰 작성시 포인트가 적립됩니다.
          </p>

          {/* 리뷰 작성 */}
          <div style={{ border: '1px solid #eee', borderRadius: 8, padding: 16, marginBottom: 20, background: '#fafafa' }}>
            <div style={{ display: 'flex', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
              <input value={reviewNick} onChange={e => setReviewNick(e.target.value)} placeholder="닉네임"
                style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, width: 120 }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {[1,2,3,4,5].map(i => (
                  <span key={i} onClick={() => setReviewRating(i)}
                    style={{ cursor: 'pointer', fontSize: 22, color: i <= reviewRating ? '#ffa41c' : '#ddd' }}>★</span>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <textarea value={reviewText} onChange={e => setReviewText(e.target.value)} placeholder="리뷰를 작성해주세요..."
                style={{ flex: 1, padding: '10px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, minHeight: 60, resize: 'vertical' }} />
              <button onClick={async () => {
                if (!reviewText.trim()) return;
                const res = await fetch(`${API_URL}/api/storefront/reviews/${id}`, {
                  method: 'POST', headers: {'Content-Type':'application/json'},
                  body: JSON.stringify({ nickname: reviewNick || '익명', rating: reviewRating, text: reviewText })
                });
                if (res.ok) {
                  const d = await res.json();
                  setReviews(d);
                  setReviewText('');
                }
              }} style={{ padding: '10px 20px', background: '#e62e04', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                등록
              </button>
            </div>
          </div>

          {/* 리뷰 목록 */}
          {reviews.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>💬</div>
              아직 작성된 리뷰가 없습니다.
            </div>
          ) : (
            <div style={{ borderTop: '1px solid #eee' }}>
              {reviews.map((r, i) => (
                <div key={i} style={{ padding: '16px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{r.nickname}</span>
                    <span style={{ color: '#ffa41c', fontSize: 14 }}>{'★'.repeat(r.rating)}{'☆'.repeat(5-r.rating)}</span>
                    <span style={{ color: '#bbb', fontSize: 12, marginLeft: 'auto' }}>{r.date}</span>
                  </div>
                  <p style={{ fontSize: 14, color: '#444', margin: 0, textAlign: 'left' }}>{r.text}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Q&A */}
        <div style={{ maxWidth: 900, margin: '40px auto', padding: '0 20px' }}>
          <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Q&A</h3>
          <p style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>
            구매하시려는 상품에 대해 궁금한 점이 있으신 경우 문의해주세요.
          </p>

          {/* Q&A 작성 */}
          <div style={{ border: '1px solid #eee', borderRadius: 8, padding: 16, marginBottom: 20, background: '#fafafa' }}>
            <input value={qnaNick} onChange={e => setQnaNick(e.target.value)} placeholder="닉네임"
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, width: 120, marginBottom: 10 }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <textarea value={qnaText} onChange={e => setQnaText(e.target.value)} placeholder="질문을 작성해주세요..."
                style={{ flex: 1, padding: '10px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, minHeight: 60, resize: 'vertical' }} />
              <button onClick={async () => {
                if (!qnaText.trim()) return;
                const res = await fetch(`${API_URL}/api/storefront/qna/${id}`, {
                  method: 'POST', headers: {'Content-Type':'application/json'},
                  body: JSON.stringify({ nickname: qnaNick || '익명', question: qnaText })
                });
                if (res.ok) {
                  const d = await res.json();
                  setQnas(d);
                  setQnaText('');
                }
              }} style={{ padding: '10px 20px', background: '#333', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                문의하기
              </button>
            </div>
          </div>

          {/* Q&A 목록 */}
          {qnas.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>❓</div>
              아직 등록된 문의가 없습니다.
            </div>
          ) : (
            <div style={{ borderTop: '1px solid #eee' }}>
              {qnas.map((q, i) => (
                <div key={i} style={{ padding: '16px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ background: '#e62e04', color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 6px', borderRadius: 3 }}>Q</span>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{q.nickname}</span>
                    <span style={{ color: '#bbb', fontSize: 12, marginLeft: 'auto' }}>{q.date}</span>
                  </div>
                  <p style={{ fontSize: 14, color: '#444', margin: '0 0 8px 0', textAlign: 'left' }}>{q.question}</p>
                  {q.answer && (
                    <div style={{ background: '#f5f5f5', padding: '10px 14px', borderRadius: 6, marginLeft: 20 }}>
                      <span style={{ background: '#03c75a', color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 6px', borderRadius: 3, marginRight: 8 }}>A</span>
                      <span style={{ fontSize: 14, color: '#333' }}>{q.answer}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ProductDetail;
